import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonButton from "../button";
import DragDatePreviewer from "../datepicker/dragDatePickerPreviewer";
import {
  downloadEmployeeStatementAction,
  downloadEmployeeStatementSuccessAction,
  downloadEmployeeStatementErrorAction,
} from "../../containers/dashboard/action";

function getModalStyle() {
  const top = 50;
  const left = 50;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}


const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const useStyles = makeStyles(() => ({
  modalPaper: { position: "absolute" },
  container: {
    
    width: "min(880px, 96vw)",
    maxHeight: "90vh",
    overflowX: "auto",
    overflowY: "auto",
    boxSizing: "border-box",
    backgroundColor: theme.colors.White,
    boxShadow: "0px 0px 10px #CFD9EA80",
    borderRadius: "15px",
    padding: "36px 30px",
  },
  
  datepickerWrap: {
    minWidth: "800px",
    paddingBottom: "6px",
  },
  title: {
    color: theme.colors.DodgerBlue,
    fontSize: "22px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "6px",
  },
  subtitle: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    marginBottom: "22px",
  },
  label: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    marginBottom: "8px",
  },
  hint: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: "#8892a4",
    marginTop: "10px",
  },
  errorBox: {
    backgroundColor: "#FDECEC",
    border: `1px solid ${theme.colors.RejectRed}`,
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "18px",
  },
  errorLine: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.RejectRed,
  },
  actions: { display: "flex", gap: "12px", marginTop: "26px" },
}));


function DownloadStatementModal({ open, onClose, employeeId, employeeName }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [modalStyle] = useState(getModalStyle);

  // Default to a month-to-date range so the picker shows a valid span on open.
  const now = new Date();
  const [startTime, setStartTime] = useState(
    fmtDate(new Date(now.getFullYear(), now.getMonth(), 1))
  );
  const [endTime, setEndTime] = useState(fmtDate(now));
  const [clientError, setClientError] = useState(null);

  const loading = useSelector(
    (state) => !!state.dashboardReducer?.downloadEmployeeStatementLoading
  );
  const success = useSelector(
    (state) => state.dashboardReducer?.downloadEmployeeStatementSuccess
  );
  const error = useSelector(
    (state) => state.dashboardReducer?.downloadEmployeeStatementError
  );

  // Clear any stale redux state each time the modal opens.
  useEffect(() => {
    if (open) {
      dispatch(downloadEmployeeStatementSuccessAction(null));
      dispatch(downloadEmployeeStatementErrorAction(null));
      setClientError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Download succeeded (the file is already saving) — toast and close.
  useEffect(() => {
    if (!success) return;
    enqueueSnackbar("Payment statement downloaded.", { variant: "success" });
    dispatch(downloadEmployeeStatementSuccessAction(null));
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const handleClose = () => {
    if (loading) return; // don't let the modal close mid-download
    dispatch(downloadEmployeeStatementErrorAction(null));
    setClientError(null);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setClientError(null);
    // Client-side guard mirroring the API (the range picker already enforces
    // start < end, but this backs it up).
    if (startTime && endTime && startTime >= endTime) {
      setClientError("Start date must be earlier than end date.");
      return;
    }
    if (!employeeId) return;
    dispatch(
      downloadEmployeeStatementAction({
        employeeId,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
      })
    );
  };

  // Collect every message to surface: client guard, general API messages, and
  // per-field validation (start_time / end_time) from the 400 details.
  const asText = (v) => (Array.isArray(v) ? v.join(" ") : v);
  const errorLines = [];
  if (clientError) errorLines.push(clientError);
  if (Array.isArray(error?.messages)) errorLines.push(...error.messages.filter(Boolean));
  if (error?.details?.start_time)
    errorLines.push(`Start date: ${asText(error.details.start_time)}`);
  if (error?.details?.end_time)
    errorLines.push(`End date: ${asText(error.details.end_time)}`);
  // De-dupe (general messages often echo the field ones).
  const uniqueErrorLines = [...new Set(errorLines)];

  return (
    <Modal open={!!open} onClose={handleClose}>
      <div style={modalStyle} className={classes.modalPaper}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <div className={classes.container}>
            <div className={classes.title}>Download payment statement</div>
            <div className={classes.subtitle}>
              {employeeName
                ? `Generate a PDF statement for ${employeeName}.`
                : "Generate a PDF statement for this employee."}
            </div>

            {uniqueErrorLines.length ? (
              <div className={classes.errorBox}>
                {uniqueErrorLines.map((m, i) => (
                  <div className={classes.errorLine} key={i}>
                    {m}
                  </div>
                ))}
              </div>
            ) : null}

            <div className={classes.label}>Date range</div>
            <div className={classes.datepickerWrap}>
              <DragDatePreviewer
                showOnTop={false}
                absoluteCalendar={false}
                startDateValue={startTime}
                endDateValue={endTime}
                setStartDateValue={setStartTime}
                setEndDateValue={setEndTime}
              />
            </div>
            <div className={classes.hint}>
              Pick the period to include in the statement.
            </div>

            <div className={classes.actions}>
              <CommonButton
                type="submit"
                text="Download PDF"
                stylevariant="secondary1"
                loading={loading}
                disabled={loading}
              />
              <CommonButton
                type="button"
                text="Cancel"
                stylevariant="secondary2"
                disabled={loading}
                onClick={handleClose}
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default DownloadStatementModal;
